import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { QRCodeSVG } from "qrcode.react"
import { fetchReturnChoice, chooseCubbito, chooseCarrierLabel } from "../services/orderServices"
import { buildDirectionsUrl } from "../utils/maps"
import { Loader } from "../components/Loader"
import { LogoHeader } from "../components/LogoHeader"
import { Button } from "../components/Button"
import Timeline, { TIMELINE_STATUS_COMPLETE, TIMELINE_STATUS_CURRENT, TIMELINE_STATUS_UPCOMING } from "../components/Timeline/Timeline"

const RECEIVED_STATUSES = ['ARRIVED_AT_WAREHOUSE', 'AWAITING_INSPECTION', 'RECEIVED', 'REENTERED']
const STALE_CODES = ['CARRIER_LABEL_ALREADY_CHOSEN', 'RETURN_CLOSED', 'RETURN_CHOICE_STALE']

const isNotFound = (error) => error?.response?.status === 404

// The returnChoice.errors key for a failed POST (404 never gets here: it shows the invalid-link view).
const bannerKey = (error, choice) => {
    const status = error?.response?.status
    const code = error?.response?.data?.code
    if (status === 409 && code === 'CUBBITO_UNAVAILABLE') return 'cubbito_unavailable'
    if (status === 409 && code === 'RETURN_CHOICE_IN_PROGRESS') return 'in_progress'
    if (status === 409 && code === 'CUBBITO_ALREADY_DROPPED_OFF') return 'already_dropped_off'
    if (status === 409 && STALE_CODES.includes(code)) return 'stale'
    if (status === 422 && choice === 'carrier_label') return 'carrier_label_unavailable'
    return 'generic'
}

const closedKey = (status) => {
    if (status === 'CANCELLED') return 'closed_cancelled'
    if (RECEIVED_STATUSES.includes(status)) return 'closed_received'
    return 'closed_other'
}

const linkClasses = "inline-flex items-center justify-center min-h-[44px] px-2 text-blue-600 hover:text-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"

// A step label; a step that has not happened yet tells screen readers so (the shared Timeline only greys it).
const StepTitle = ({ label, pending, t }) => (
    <>
        <span>{label}</span>
        {pending && <span className="sr-only"> {t('returnChoice.step_pending')}</span>}
    </>
)

const PointsList = ({ points, t }) => (
    <ul className="flex flex-col space-y-3 mt-3">
        {points.map((point) => {
            const directionsUrl = buildDirectionsUrl(point)
            return (
                <li key={point.id} className="text-sm border border-gray-200 rounded-lg p-3">
                    <div className="font-semibold text-gray-800 break-words">{point.name}</div>
                    <div className="text-gray-500 mt-1 break-words">{point.address}</div>
                    {point.distance_km != null && (
                        <div className="text-gray-500 mt-1">{t('returnChoice.distance', { distance: point.distance_km.toFixed(1) })}</div>
                    )}
                    {point.schedule && (
                        <div className="text-gray-500 mt-1 break-words">{t('returnChoice.hours', { schedule: point.schedule })}</div>
                    )}
                    {directionsUrl && (
                        <a className="inline-block mt-2 text-blue-600 hover:text-blue-700" href={directionsUrl} target="_blank" rel="noopener noreferrer">
                            {t('returnChoice.directions')}
                        </a>
                    )}
                </li>
            )
        })}
    </ul>
)

export const ReturnChoiceContainer = () => {
    const { t } = useTranslation()
    const { signed_id } = useParams()
    const queryClient = useQueryClient()
    const key = ['returnChoice', signed_id]
    const [confirmingDhl, setConfirmingDhl] = useState(false)

    const query = useQuery(key, () => fetchReturnChoice(signed_id), { retry: false })

    // react-query awaits these callbacks before settling the mutation, so the button stays
    // disabled through the refetch and the banner appears over the refetched state.
    const mutation = useMutation(
        (choice) => (choice === 'cubbito' ? chooseCubbito(signed_id) : chooseCarrierLabel(signed_id)),
        {
            onSuccess: async (body) => {
                // An older in-flight GET must not overwrite the POST result.
                await queryClient.cancelQueries(key)
                queryClient.setQueryData(key, body)
            },
            onError: async (error) => {
                if (isNotFound(error)) return
                // The choice may have committed before its response was lost: show the true state.
                await queryClient.cancelQueries(key)
                await queryClient.refetchQueries(key)
                setConfirmingDhl(false)
            },
        }
    )

    const data = query.data
    const busy = mutation.isLoading

    // Moving between steps drops the banner of an earlier attempt.
    const showConfirmDhl = (show) => {
        mutation.reset()
        setConfirmingDhl(show)
    }

    if (isNotFound(query.error) || isNotFound(mutation.error)) {
        return <Message text={t('returnChoice.not_found')} />
    }
    if (!data) {
        if (query.isError) {
            return (
                <Message text={t('returnChoice.load_error')}>
                    <Button variant="outline" size="md" className="mt-5" label={t('returnChoice.retry')} onClick={() => query.refetch()} />
                </Message>
            )
        }
        return <Loader show={true} />
    }

    const renderConfirmDhl = () => (
        <Card>
            <h2 className="font-semibold text-gray-800">{t('returnChoice.confirm_dhl_title')}</h2>
            <p className="text-sm text-gray-500 mt-2">{t('returnChoice.confirm_dhl_description')}</p>
            <Button label={t('returnChoice.confirm')} disabled={busy} onClick={() => mutation.mutate('carrier_label')} />
            <div className="text-center mt-3">
                <button type="button" className={linkClasses} disabled={busy} onClick={() => showConfirmDhl(false)}>
                    {t('returnChoice.back')}
                </button>
            </div>
        </Card>
    )

    const renderPending = () => (
        <>
            <h2 className="font-semibold text-gray-800">{t('returnChoice.choose_title')}</h2>
            <Card>
                <h3 className="font-semibold text-gray-800">{t('returnChoice.cubbito_title')}</h3>
                {data.cubbito?.available ? (
                    <>
                        <p className="text-sm text-gray-500 mt-1">{t('returnChoice.cubbito_description')}</p>
                        <PointsList points={data.cubbito.points} t={t} />
                        <Button label={t('returnChoice.choose_cubbito')} disabled={busy} onClick={() => mutation.mutate('cubbito')} />
                    </>
                ) : (
                    <>
                        <p className="text-sm font-medium text-gray-700 mt-1">{t('returnChoice.cubbito_unavailable')}</p>
                        <p className="text-sm text-gray-500 mt-1">{t('returnChoice.cubbito_unavailable_description')}</p>
                    </>
                )}
            </Card>
            <Card>
                <h3 className="font-semibold text-gray-800">{t('returnChoice.dhl_title')}</h3>
                <p className="text-sm text-gray-500 mt-1">{t('returnChoice.dhl_description')}</p>
                <Button variant="outline" label={t('returnChoice.choose_dhl')} disabled={busy} onClick={() => showConfirmDhl(true)} />
            </Card>
        </>
    )

    const renderCubbito = () => (
        <>
            <Card>
                <h2 className="font-semibold text-gray-800">{t('returnChoice.cubbito_ready_title')}</h2>
                <div className="flex justify-center mt-4">
                    <QRCodeSVG value={data.cubbito.dropoff_code} size={192} title={t('returnChoice.code_label')} />
                </div>
                <div className="text-center mt-4">
                    <div className="text-sm text-gray-500">{t('returnChoice.code_label')}</div>
                    <div className="font-mono text-2xl font-semibold text-gray-800 break-all">{data.cubbito.dropoff_code_display}</div>
                </div>
            </Card>
            <Card>
                <h3 className="font-semibold text-gray-800">{t('returnChoice.packing_title')}</h3>
                <p className="text-sm text-gray-500 mt-1">{t('returnChoice.packing_rule')}</p>
            </Card>
            <Card>
                <h3 className="font-semibold text-gray-800">{t('returnChoice.points_title')}</h3>
                {data.cubbito.points?.length
                    ? <PointsList points={data.cubbito.points} t={t} />
                    : <p className="text-sm text-gray-500 mt-1">{t('returnChoice.points_unavailable')}</p>}
            </Card>
            <div className="text-center">
                <button type="button" className={linkClasses} disabled={busy} onClick={() => showConfirmDhl(true)}>
                    {t('returnChoice.switch_to_dhl')}
                </button>
            </div>
        </>
    )

    const renderDroppedOff = () => {
        const { dropped_off_at, dropoff_point_name, recollected_at } = data.cubbito
        const collected = Boolean(recollected_at)
        const steps = [
            {
                name: 'dropped_off',
                status: collected ? TIMELINE_STATUS_COMPLETE : TIMELINE_STATUS_CURRENT,
                currentStatusColor: 'green',
                reference_timestamp: dropped_off_at,
                title: <StepTitle label={t('returnChoice.step_dropped_off', { point: dropoff_point_name })} t={t} />,
            },
            {
                name: 'on_the_way',
                status: collected ? TIMELINE_STATUS_CURRENT : TIMELINE_STATUS_UPCOMING,
                currentStatusColor: 'green',
                reference_timestamp: recollected_at,
                title: <StepTitle label={t('returnChoice.step_on_the_way')} pending={!collected} t={t} />,
            },
            {
                name: 'received',
                status: TIMELINE_STATUS_UPCOMING,
                title: <StepTitle label={t('returnChoice.step_received')} pending t={t} />,
            },
        ]
        return (
            <Card>
                <h2 className="font-semibold text-gray-800">{t('returnChoice.dropped_off_title')}</h2>
                <div className="mt-4">
                    <Timeline steps={steps} />
                </div>
            </Card>
        )
    }

    const renderCarrierLabel = () => {
        const label = data.carrier_label
        return (
            <Card>
                <h2 className="font-semibold text-gray-800">{t('returnChoice.dhl_ready_title')}</h2>
                <p className="text-sm text-gray-500 mt-1">{t('returnChoice.dhl_ready_description')}</p>
                {label && (
                    <dl className="text-sm mt-3 space-y-2">
                        <div>
                            <dt className="text-gray-500">{t('returnChoice.carrier')}</dt>
                            <dd className="text-gray-800 break-words">{label.carrier_name}</dd>
                        </div>
                        <div>
                            <dt className="text-gray-500">{t('returnChoice.tracking_number')}</dt>
                            <dd className="text-gray-800 font-mono break-all">{label.shipping_number}</dd>
                        </div>
                    </dl>
                )}
                {label?.label_url && (
                    <a
                        className="block text-center py-3 px-5 mt-5 w-full rounded-lg shadow-sm border border-transparent bg-blue-500 text-white hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:outline-none focus:ring-blue-600"
                        href={label.label_url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('returnChoice.open_label')}
                    </a>
                )}
            </Card>
        )
    }

    const renderBody = () => {
        if (data.status !== 'REQUESTED') {
            return <Card><p className="text-sm text-gray-700">{t(`returnChoice.${closedKey(data.status)}`)}</p></Card>
        }
        // Keyed only on dropped_off_at: a racing choose-Cubbito can still return points with it.
        if (data.cubbito?.dropped_off_at) return renderDroppedOff()
        if (data.choice === 'carrier_label') return renderCarrierLabel()
        if (confirmingDhl) return renderConfirmDhl()
        if (data.choice === 'cubbito') return renderCubbito()
        return renderPending()
    }

    return (
        <Page>
            <h1 className="text-xl font-semibold text-gray-800">{t('returnChoice.title')}</h1>
            <p className="text-sm text-gray-500 mt-1 break-words">
                {t('returnChoice.order', { order_number: data.order_number, store: data.store_name })}
            </p>
            <div className="mt-4">
                <h2 className="text-sm font-medium text-gray-700">{t('returnChoice.items_title')}</h2>
                <ul className="text-sm text-gray-600 mt-1">
                    {(data.products || []).map((product, index) => (
                        <li key={index} className="break-words">{t('returnChoice.item', { quantity: product.quantity, name: product.name })}</li>
                    ))}
                </ul>
            </div>
            {mutation.isError && (
                <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4 text-sm text-red-600">
                    {t(`returnChoice.errors.${bannerKey(mutation.error, mutation.variables)}`)}
                </div>
            )}
            <div className="flex flex-col space-y-4 mt-5">{renderBody()}</div>
        </Page>
    )
}

const Page = ({ children }) => (
    <div className="py-5 px-5 sm:max-w-[480px]">
        <LogoHeader />
        <div className="mt-5">{children}</div>
    </div>
)

const Card = ({ children }) => <div className="rounded-lg border bg-white p-4">{children}</div>

const Message = ({ text, children }) => (
    <Page>
        <div className="flex flex-col items-center py-20 text-center">
            <p className="text-sm text-gray-500">{text}</p>
            {children}
        </div>
    </Page>
)
